import { UNIT_LABELS, UNIT_TYPES, type UnitType } from '@bienenkampf/shared';
import { flightTimeMinutes, hexDistance, totalOf, type UnitCounts } from '@bienenkampf/sim';
import { Badge, Button, Card, Input } from '@bienenkampf/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { RELATION_LABELS } from '@bienenkampf/shared';
import { useEffect, useMemo, useState } from 'react';
import { useNow } from '../hooks/useNow.js';
import { formatAbsRel, formatDuration, formatNumber } from '../lib/time.js';
import { useTRPC } from '../lib/trpc.js';
import { ColonizeForm } from './ColonizePanel.js';
import { CombatSimulator, UnitInputs, draftToCounts, type UnitDraft } from './CombatSimulator.js';

export interface PanelHive {
  id: number;
  q: number;
  r: number;
  name: string;
  points: number;
  ownerName: string | null;
  ownerPoints: number | null;
  isMine: boolean;
  protectionUntil: Date | null;
  shieldUntil: Date | null;
  ownerId?: number | null;
  allianceTag?: string | null;
  relation?: 'ALLIED' | 'NEUTRAL' | 'HOSTILE';
  queenlessSince?: Date | null;
}

export function HivePanel({
  worldId,
  hive,
  myHive,
}: {
  worldId: number;
  hive: PanelHive;
  myHive: { id: number; q: number; r: number } | undefined;
}) {
  const now = useNow();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [tab, setTab] = useState<'raid' | 'scout' | 'colonize' | 'support'>('raid');
  const [supportDraft, setSupportDraft] = useState<UnitDraft>({});
  const [planAt, setPlanAt] = useState('');
  const [draft, setDraft] = useState<UnitDraft>({});
  const [scouts, setScouts] = useState('');
  const [defDraft, setDefDraft] = useState<UnitDraft>({});
  const [guardComb, setGuardComb] = useState(0);
  const [winter, setWinter] = useState(false);
  const [queenId, setQueenId] = useState('');
  const navigate = useNavigate();
  const sendQueen = useMutation(
    trpc.hive.queen.send.mutationOptions({
      onSuccess: () => {
        setQueenId('');
        void qc.invalidateQueries({ queryKey: trpc.movement.pathKey() });
        void qc.invalidateQueries({ queryKey: trpc.hive.get.pathKey() });
      },
    }),
  );
  const support = useMutation(
    trpc.movement.sendSupport.mutationOptions({
      onSuccess: () => {
        setSupportDraft({});
        invalidate();
      },
    }),
  );
  const plan = useMutation(
    trpc.alliance.createPlan.mutationOptions({
      onSettled: () => qc.invalidateQueries({ queryKey: trpc.alliance.pathKey() }),
    }),
  );
  const openDm = useMutation(
    trpc.chat.openDm.mutationOptions({
      onSuccess: (r) =>
        void navigate({
          to: '/w/$worldId/chat',
          params: { worldId: String(worldId) },
          search: { c: r.conversationId },
        }),
    }),
  );

  const mine = useQuery({
    ...trpc.hive.get.queryOptions({ hiveId: myHive?.id ?? 0 }),
    enabled: !!myHive,
  });
  const cfg = useQuery(trpc.world.config.queryOptions({ worldId }));
  const worlds = useQuery(trpc.world.list.queryOptions());
  const lastScout = useQuery({
    ...trpc.report.latestScout.queryOptions({ worldId, targetHiveId: hive.id }),
    enabled: !!myHive && !hive.isMine,
  });
  const world = worlds.data?.find((w) => w.id === worldId);
  const seasonKey = world?.season.season ?? 'TRACHT';
  useEffect(() => setWinter(seasonKey === 'WINTER'), [seasonKey]);

  // Spaehbericht als Vorbelegung fuer den Simulator
  useEffect(() => {
    const p = lastScout.data?.payload as
      { target?: { units?: UnitCounts; buildings?: Record<string, number> } } | undefined;
    if (p?.target?.units) {
      const d: UnitDraft = {};
      for (const t of UNIT_TYPES)
        if ((p.target.units[t] ?? 0) > 0) d[t] = String(p.target.units[t]);
      setDefDraft(d);
      setGuardComb(p.target.buildings?.GUARD_COMB ?? 0);
    }
  }, [lastScout.data]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: trpc.movement.pathKey() });
    if (myHive)
      void qc.invalidateQueries({ queryKey: trpc.hive.get.queryKey({ hiveId: myHive.id }) });
    void qc.invalidateQueries({ queryKey: trpc.auth.me.queryKey() });
  };
  const raid = useMutation(
    trpc.movement.sendRaid.mutationOptions({
      onSuccess: () => {
        setDraft({});
        invalidate();
      },
    }),
  );
  const scout = useMutation(
    trpc.movement.sendScout.mutationOptions({
      onSuccess: () => {
        setScouts('');
        invalidate();
      },
    }),
  );

  const available = useMemo(() => {
    const out: Partial<Record<UnitType, number>> = {};
    for (const u of mine.data?.units ?? []) out[u.type] = u.count;
    return out;
  }, [mine.data]);
  const attackers = draftToCounts(draft);
  const tooMany = UNIT_TYPES.some((t) => (attackers[t] ?? 0) > (available[t] ?? 0));
  const slowest = UNIT_TYPES.reduce(
    (m, t) =>
      (attackers[t] ?? 0) > 0 && cfg.data ? Math.max(m, cfg.data.balance.units[t].minPerHex) : m,
    0,
  );
  const minutes = (perHex: number) =>
    myHive && cfg.data
      ? flightTimeMinutes({
          from: myHive,
          to: hive,
          slowestMinPerHex: perHex,
          seasonSpeedMult: Number(cfg.data.seasons[seasonKey].speedMult),
          worldSpeed: cfg.data.speed,
        })
      : 0;
  const protectedNow = !!hive.protectionUntil && hive.protectionUntil > now;
  const shieldedNow = !!hive.shieldUntil && hive.shieldUntil > now;
  const scoutsAvail = available.SCOUT ?? 0;
  const nScouts = Math.max(0, Math.floor(Number(scouts) || 0));

  return (
    <Card
      title={hive.name}
      action={
        hive.isMine ? (
          <Badge tone="amber">Dein Stock</Badge>
        ) : (
          <span className="flex items-center gap-1">
            {hive.allianceTag && <Badge>[{hive.allianceTag}]</Badge>}
            {hive.ownerId == null ? (
              <Badge>unbewohnt</Badge>
            ) : (
              <Badge>{hive.ownerName ?? '–'}</Badge>
            )}
            {hive.relation && hive.relation !== 'NEUTRAL' && (
              <Badge tone={hive.relation === 'ALLIED' ? 'green' : 'red'}>
                {RELATION_LABELS[hive.relation]}
              </Badge>
            )}
          </span>
        )
      }
    >
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
        <dt className="text-stone-400">Position</dt>
        <dd>
          [{hive.q}|{hive.r}]
        </dd>
        <dt className="text-stone-400">Punkte</dt>
        <dd>
          {formatNumber(hive.points)}{' '}
          {hive.ownerPoints !== null && `· Königin ${formatNumber(hive.ownerPoints)}`}
        </dd>
        {myHive && !hive.isMine && (
          <>
            <dt className="text-stone-400">Entfernung</dt>
            <dd>{hexDistance(myHive, hive)} Hex</dd>
          </>
        )}
      </dl>
      <div className="mt-2 flex flex-wrap gap-1">
        {protectedNow && (
          <Badge tone="green">Imkerschutz {formatAbsRel(hive.protectionUntil!, now)}</Badge>
        )}
        {shieldedNow && (
          <Badge tone="sky">Schutzschild {formatAbsRel(hive.shieldUntil!, now)}</Badge>
        )}
        {hive.queenlessSince && <Badge tone="red">👑 weisellos – übernehmbar</Badge>}
      </div>

      {hive.isMine && (
        <p className="mt-3 text-sm text-stone-400">
          <Link
            to="/w/$worldId/stock"
            params={{ worldId: String(worldId) }}
            className="text-amber-400"
          >
            Zum Stock
          </Link>
        </p>
      )}

      {myHive && hive.ownerId == null && (
        <div className="mt-3 flex flex-col gap-3">
          <p className="text-sm text-stone-300">
            🏚 Unbewohnter Stock. Eine Reservekönigin mit Hofstaat kann hier eine Kolonie gründen.
          </p>
          <ColonizeForm
            worldId={worldId}
            myHive={myHive}
            target={{
              hiveId: hive.id,
              q: hive.q,
              r: hive.r,
              points: 0,
              name: hive.name,
              free: true,
            }}
          />
        </div>
      )}
      {myHive && !hive.isMine && hive.ownerId != null && (
        <div className="mt-3 flex flex-col gap-3">
          {hive.ownerId != null && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => openDm.mutate({ worldId, otherPlayerId: hive.ownerId! })}
              loading={openDm.isPending}
            >
              ✉ Nachricht an {hive.ownerName ?? 'Königin'}
            </Button>
          )}
          {hive.relation === 'ALLIED' && (mine.data?.queen.reserve.length ?? 0) > 0 && (
            <div className="rounded-lg border border-emerald-900/60 bg-emerald-950/30 p-2 text-sm">
              <div className="mb-1 text-xs text-emerald-300">
                Reservekönigin an diesen verbündeten Stock liefern
              </div>
              <div className="flex items-center gap-2">
                <select
                  className="flex-1 rounded-md border border-stone-700 bg-stone-900 px-2 py-1 text-sm"
                  value={queenId}
                  onChange={(e) => setQueenId(e.target.value)}
                >
                  <option value="">Königin wählen…</option>
                  {mine.data!.queen.reserve.map((q) => (
                    <option key={q.id} value={q.id}>
                      ♛ {q.name}
                    </option>
                  ))}
                </select>
                <Button
                  size="sm"
                  disabled={!queenId || sendQueen.isPending}
                  loading={sendQueen.isPending}
                  onClick={() =>
                    sendQueen.mutate({
                      hiveId: myHive.id,
                      queenId: Number(queenId),
                      targetHiveId: hive.id,
                      units: {},
                    })
                  }
                >
                  👑 Liefern
                </Button>
              </div>
              {sendQueen.error && (
                <p className="mt-1 text-xs text-red-400">{sendQueen.error.message}</p>
              )}
              {sendQueen.isSuccess && (
                <p className="mt-1 text-xs text-emerald-400">Unterwegs. Details unter „Züge".</p>
              )}
            </div>
          )}
          <div className="flex gap-1 rounded-lg bg-stone-900 p-1 text-sm">
            {(
              (hive.relation === 'ALLIED'
                ? ['support', 'scout']
                : hive.queenlessSince
                  ? ['raid', 'scout', 'colonize']
                  : ['raid', 'scout']) as Array<'raid' | 'scout' | 'colonize' | 'support'>
            ).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={`flex-1 rounded-md px-3 py-1.5 ${tab === t ? 'bg-stone-800 text-amber-300' : 'text-stone-300'}`}
              >
                {t === 'raid'
                  ? '⚔ Raubzug'
                  : t === 'scout'
                    ? '🔭 Ausspähen'
                    : t === 'support'
                      ? '🛡 Unterstützen'
                      : '👑 Übernehmen'}
              </button>
            ))}
          </div>

          {tab === 'raid' && (
            <form
              className="flex flex-col gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (totalOf(attackers) > 0 && !tooMany)
                  raid.mutate({
                    worldId,
                    hiveId: myHive.id,
                    targetHiveId: hive.id,
                    units: attackers,
                  });
              }}
            >
              <UnitInputs value={draft} onChange={setDraft} max={available} />
              {totalOf(attackers) > 0 && (
                <p className="text-xs text-stone-400">
                  Ankunft {formatAbsRel(new Date(now.getTime() + minutes(slowest) * 60_000), now)} (
                  {formatDuration(minutes(slowest) * 60)}) · langsamste Biene {slowest} min/Hex
                </p>
              )}
              {cfg.data && (
                <CombatSimulator
                  balance={cfg.data.balance}
                  attackers={attackers}
                  defenders={defDraft}
                  onDefendersChange={setDefDraft}
                  guardCombLevel={guardComb}
                  onGuardCombChange={setGuardComb}
                  winter={winter}
                  onWinterChange={setWinter}
                  seasons={cfg.data.seasons}
                />
              )}
              {lastScout.data && (
                <p className="text-xs text-stone-500">
                  Verteidiger vorbelegt aus Spähbericht vom{' '}
                  {formatAbsRel(lastScout.data.createdAt, now)}.
                </p>
              )}
              <Button
                type="submit"
                variant="danger"
                disabled={
                  totalOf(attackers) === 0 ||
                  tooMany ||
                  protectedNow ||
                  shieldedNow ||
                  raid.isPending
                }
                loading={raid.isPending}
                title={
                  protectedNow ? 'Imkerschutz' : shieldedNow ? 'Schutzschild aktiv' : undefined
                }
              >
                Raubzug starten
              </Button>
              {raid.error && <p className="text-sm text-red-400">{raid.error.message}</p>}
              {raid.isSuccess && (
                <p className="text-sm text-emerald-400">
                  Unterwegs. Der Bericht kommt nach der Ankunft.
                </p>
              )}
            </form>
          )}

          {tab === 'support' && (
            <form
              className="flex flex-col gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                const u = draftToCounts(supportDraft);
                if (totalOf(u) > 0)
                  support.mutate({ worldId, hiveId: myHive.id, targetHiveId: hive.id, units: u });
              }}
            >
              <p className="text-xs text-stone-400">
                Die Bienen verteidigen diesen verbündeten Stock mit, bis du sie unter „Züge“
                zurückrufst oder der Gastgeber sie heimschickt.
              </p>
              <UnitInputs value={supportDraft} onChange={setSupportDraft} max={available} />
              <Button
                type="submit"
                disabled={totalOf(draftToCounts(supportDraft)) === 0 || support.isPending}
                loading={support.isPending}
              >
                Unterstützung schicken
              </Button>
              {support.error && <p className="text-sm text-red-400">{support.error.message}</p>}
              {support.isSuccess && <p className="text-sm text-emerald-400">Unterwegs.</p>}
            </form>
          )}
          {tab === 'raid' && hive.relation !== 'ALLIED' && (
            <div className="flex flex-wrap items-end gap-2 rounded-lg bg-stone-900 p-2 text-xs">
              <label className="flex flex-col gap-1">
                <span className="text-stone-400">Im Angriffsplaner eintragen (Ankunft)</span>
                <input
                  type="datetime-local"
                  className="rounded-md border border-stone-700 bg-stone-950 px-2 py-1 text-sm"
                  value={planAt}
                  onChange={(e) => setPlanAt(e.target.value)}
                />
              </label>
              <Button
                size="sm"
                variant="ghost"
                disabled={!planAt || plan.isPending}
                loading={plan.isPending}
                onClick={() =>
                  plan.mutate({
                    worldId,
                    kind: 'ATTACK',
                    targetHiveId: hive.id,
                    deadline: new Date(planAt),
                    note: `Ziel ${hive.name} (${hive.ownerName ?? '–'})`,
                  })
                }
              >
                📋 Eintragen
              </Button>
              {plan.isSuccess && <span className="text-emerald-400">Im Planer.</span>}
              {plan.error && <span className="text-red-400">{plan.error.message}</span>}
            </div>
          )}
          {tab === 'colonize' && hive.queenlessSince && (
            <div className="flex flex-col gap-2">
              <p className="text-xs text-stone-400">
                Der Stock ist weisellos seit {formatAbsRel(hive.queenlessSince, now)}. Solange keine
                Königin nachrückt, kann eine Reservekönigin mit Hofstaat ihn übernehmen: Lager leer,
                Waben halbiert, 6 h Schild gegen Rückeroberung.
                {lastScout.data
                  ? ' Restbevölkerung und Wächterwabe aus dem letzten Spähbericht.'
                  : ' Ohne Spähbericht wird mit leerem Stock gerechnet – erst spähen!'}
              </p>
              <ColonizeForm
                worldId={worldId}
                myHive={myHive}
                target={{
                  hiveId: hive.id,
                  q: hive.q,
                  r: hive.r,
                  points: hive.points,
                  name: hive.name,
                  free: false,
                }}
                remainingPop={totalOf(draftToCounts(defDraft))}
                guardCombLevel={guardComb}
              />
            </div>
          )}

          {tab === 'scout' && (
            <form
              className="flex flex-col gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (nScouts > 0 && nScouts <= scoutsAvail)
                  scout.mutate({
                    worldId,
                    hiveId: myHive.id,
                    targetHiveId: hive.id,
                    scouts: nScouts,
                  });
              }}
            >
              <p className="text-xs text-stone-400">
                Erfolg, wenn mehr Späherinnen ankommen, als der Stock zu Hause hat. Bei Misserfolg
                sind sie verloren, und der Verteidiger wird gewarnt.
              </p>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Input
                    name="scouts"
                    type="number"
                    inputMode="numeric"
                    label={`${UNIT_LABELS.SCOUT}nen (${formatNumber(scoutsAvail)} verfügbar)`}
                    min={1}
                    max={Math.max(1, scoutsAvail)}
                    value={scouts}
                    onChange={(e) => setScouts(e.target.value)}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={nScouts < 1 || nScouts > scoutsAvail || protectedNow || scout.isPending}
                  loading={scout.isPending}
                >
                  Ausspähen
                </Button>
              </div>
              {nScouts > 0 && cfg.data && (
                <p className="text-xs text-stone-400">
                  Ankunft{' '}
                  {formatAbsRel(
                    new Date(
                      now.getTime() + minutes(cfg.data.balance.units.SCOUT.minPerHex) * 60_000,
                    ),
                    now,
                  )}
                </p>
              )}
              {scoutsAvail === 0 && (
                <p className="text-xs text-amber-400">
                  Späherinnen brauchen den Tanzboden (Stufe 1).
                </p>
              )}
              {scout.error && <p className="text-sm text-red-400">{scout.error.message}</p>}
              {scout.isSuccess && <p className="text-sm text-emerald-400">Unterwegs.</p>}
            </form>
          )}
        </div>
      )}
    </Card>
  );
}
